// @ts-nocheck
import { BarChart3, Eye, Settings, UserX } from 'lucide-react';
import {
  workflowTableIconButtonEditClass,
  workflowTableIconButtonViewClass,
} from '../../../../components/WorkflowFormPrimitives';
import { StatusBadge } from '../shared/StatusBadge';
import { fmtDate, pctFmt } from '../shared/voteRoomFormat';
import { LIVE_SHAREHOLDER_GRID } from '../shared/voteRoomConstants';
import { isStrictThresholdMet, isStrictThresholdUnreachable, sumSharesByStatus } from '../shared/voteTallyMath';

/** Live Vote Room admin console: approval progress and shareholder status list. */
export const AdminDashboard = ({
  shareholders,
  eligibleShares,
  excludedDqis,
  shutdownSettings,
  onViewAs,
  onOpenSettings,
  onOpenViewActivity,
  companyName,
}) => {
  const approvedShares = sumSharesByStatus(shareholders, 'approved');
  const disapprovedShares = sumSharesByStatus(shareholders, 'disapproved');
  const abstainShares = sumSharesByStatus(shareholders, 'abstained');
  const approvedPct = eligibleShares > 0 ? approvedShares / eligibleShares : 0;
  const disapprovedPct = eligibleShares > 0 ? disapprovedShares / eligibleShares : 0;
  const abstainPct = eligibleShares > 0 ? abstainShares / eligibleShares : 0;
  const votedPct = eligibleShares > 0 ? (approvedShares + disapprovedShares + abstainShares) / eligibleShares : 0;
  const effectiveThreshold =
    shutdownSettings.mode === 'threshold_custom' ? shutdownSettings.customThreshold / 100 : 0.75;
  const thresholdMet = isStrictThresholdMet(approvedShares, eligibleShares, effectiveThreshold);
  const thresholdImpossible = isStrictThresholdUnreachable(
    disapprovedShares,
    abstainShares,
    eligibleShares,
    effectiveThreshold
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Vote Room — Live</p>
          <p className="text-xs text-gray-500 mt-0.5">{companyName} — Shareholder approval console</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={onOpenSettings}
            className="inline-flex items-center px-3 py-2 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <Settings size={13} className="mr-1.5" />
            Shutdown Rule
          </button>
        </div>
      </div>

      {excludedDqis.length > 0 && (
        <div className="bg-amber-50/60 border border-amber-100 rounded-lg px-4 py-2.5 text-xs text-amber-700 flex items-start space-x-2">
          <UserX size={13} className="mt-0.5 flex-shrink-0 text-amber-500" />
          <div>
            <span>
              <strong>{excludedDqis.length} shareholder(s) excluded</strong> from voting.
            </span>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <BarChart3 size={14} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-900">Approval Progress</span>
          </div>
          {thresholdMet ? (
            <StatusBadge variant="approved">Threshold Met</StatusBadge>
          ) : thresholdImpossible ? (
            <StatusBadge variant="disapproved">75% Unreachable</StatusBadge>
          ) : (
            <StatusBadge variant="pending">In Progress</StatusBadge>
          )}
        </div>
        <div className="relative h-6 bg-gray-100 rounded-md overflow-visible">
          <div
            className="absolute inset-y-0 left-0 bg-emerald-500 rounded-l-md transition-all duration-700 z-[1]"
            style={{ width: `${Math.min(approvedPct * 100, 100)}%` }}
          />
          <div
            className="absolute inset-y-0 bg-red-400 transition-all duration-700 z-[1]"
            style={{ left: `${approvedPct * 100}%`, width: `${disapprovedPct * 100}%` }}
          />
          <div
            className="absolute inset-y-0 bg-gray-300 transition-all duration-700 z-[1]"
            style={{
              left: `${(approvedPct + disapprovedPct) * 100}%`,
              width: `${abstainPct * 100}%`,
            }}
          />
          <div className="absolute inset-y-0 z-[5]" style={{ left: '75%' }}>
            <div className="absolute -top-1 -bottom-1 w-0.5 bg-gray-800" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <span>Approved {pctFmt(approvedPct)}</span>
          <span>Disapproved {pctFmt(disapprovedPct)}</span>
          <span>Abstain {pctFmt(abstainPct)}</span>
          <span>Pending {pctFmt(Math.max(0, 1 - votedPct))}</span>
        </div>
      </div>

      <div
        className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-x-auto"
        role="table"
        aria-label="Shareholders"
      >
        <div
          className="min-w-[44rem] grid items-center gap-x-4 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
          style={{ gridTemplateColumns: LIVE_SHAREHOLDER_GRID }}
          role="row"
        >
          <span role="columnheader">Name</span>
          <span className="text-right" role="columnheader">
            Shares
          </span>
          <span className="text-right" role="columnheader">
            % Eligible
          </span>
          <span role="columnheader">Signed</span>
          <span role="columnheader">Status</span>
          <span className="sr-only" role="columnheader">
            Actions
          </span>
        </div>
        <div className="min-w-[44rem] divide-y divide-gray-50" role="rowgroup">
          {shareholders.map((sh) => (
            <div
              key={sh.id}
              className="grid items-center gap-x-4 px-4 py-3 hover:bg-gray-50/40"
              style={{ gridTemplateColumns: LIVE_SHAREHOLDER_GRID }}
              role="row"
            >
              <div className="min-w-0" role="cell">
                <div className="text-xs font-semibold text-gray-900 truncate" title={sh.name}>
                  {sh.name}
                </div>
                <div className="text-xs text-gray-500 truncate" title={sh.email}>
                  {sh.email}
                </div>
              </div>
              <div className="text-xs text-gray-700 tabular-nums text-right whitespace-nowrap" role="cell">
                {sh.shares.toLocaleString()}
              </div>
              <div className="text-xs text-gray-700 tabular-nums font-medium text-right whitespace-nowrap" role="cell">
                {eligibleShares > 0 ? pctFmt(sh.shares / eligibleShares) : '0.0%'}
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap" role="cell">
                {sh.signedAt ? fmtDate(sh.signedAt) : <span className="text-gray-300">—</span>}
              </div>
              <div role="cell">
                <StatusBadge variant={sh.status}>{sh.status}</StatusBadge>
              </div>
              <div className="flex items-center justify-end gap-1" role="cell">
                <button
                  type="button"
                  onClick={() => onOpenViewActivity(sh)}
                  className={workflowTableIconButtonViewClass}
                  title="View activity"
                  aria-label={`View activity for ${sh.name}`}
                >
                  <BarChart3 size={18} strokeWidth={2} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => onViewAs(sh)}
                  className={workflowTableIconButtonEditClass}
                  title="Preview as shareholder"
                  aria-label={`Preview vote room as ${sh.name}`}
                >
                  <Eye size={18} strokeWidth={2} aria-hidden />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
